import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Groups as GroupsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Team } from '../../models/types';
import { EmptyState } from '../ui/EmptyState';
import { SegmentedControl } from '../ui/SegmentedControl';
import { TeamStatsBarChart } from './TeamStatsBarChart';
import { TeamRosterTable } from './TeamRosterTable';
import { SnackbarState } from './useContestActions';

interface ContestTeamsTabProps {
  teams: Team[];
  contestId: string;
  groupId: string;
  // Members can be added, moved and removed until the contest ends; teams can only be deleted before it starts.
  membersLocked: boolean;
  teamsLocked: boolean;
  canManage: boolean;
  onAddMember: (team: Team) => void;
  onChanged: () => void;
  notify: (message: string, severity: SnackbarState['severity']) => void;
}

type TeamsView = 'standings' | 'roster';

export const ContestTeamsTab = ({ teams, contestId, groupId, membersLocked, teamsLocked, canManage, onAddMember, onChanged, notify }: ContestTeamsTabProps) => {
  const { t } = useTranslation('contests');
  // Before the start admins are mostly building the roster; afterwards the standings matter more.
  const [view, setView] = useState<TeamsView>(canManage && !teamsLocked ? 'roster' : 'standings');

  if (teams.length === 0) {
    return <EmptyState icon={<GroupsIcon />} title={t('teamsTab.emptyTitle')} description={t('teamsTab.emptyDescription')} />;
  }

  // The leaderboard rows double as the team list: each expands to its members and carries the add-member action.
  const standings = <TeamStatsBarChart teams={teams} contestId={contestId} membersLocked={membersLocked} onAddMember={onAddMember} />;
  if (!canManage) return standings;

  return (
    <>
      <Box sx={{ p: '16px 16px 0' }}>
        <SegmentedControl
          size="small"
          ariaLabel={t('roster.viewLabel')}
          options={[
            { value: 'roster', label: t('roster.views.roster') },
            { value: 'standings', label: t('roster.views.standings') },
          ]}
          value={view}
          onChange={setView}
        />
      </Box>
      {view === 'roster' ? (
        <TeamRosterTable contestId={contestId} groupId={groupId} teams={teams} locked={membersLocked} teamsLocked={teamsLocked} onChanged={onChanged} notify={notify} />
      ) : (
        standings
      )}
    </>
  );
};
